/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Fab,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
  InputAdornment
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from '@material-ui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AttachmentModal from 'modules/common/AttatchmentModal.component';
import AdvancedSearch from 'modules/common/advancedSearch.component';
import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import { formatAmount } from 'utilities/currencyFormatter';
import VoucherDetailsButtons from './voucherDetailsButtons.component';
import VoucherReportLineItem from './voucherReportLineItem.component';
import { categoryLabel, codeFromId, CreateExpenseCodeSearchInfo, descriptionFromId, ecById } from 'modules/common/expenseCodeHelper';
import SpinnerComponent from 'modules/common/spinner.component';
import { ZERO_GUID } from 'modules/enumerations/enumerationIds';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import { INTERNATIONAL_VOUCHER_TYPE_ID, PAYMENT_VOUCHER_TYPE_ID, USD_CURRENCY_ID } from '../voucherConstants';

const useStyles = makeStyles(theme => ({
  card: { width: '100%', flexGrow: 1, flexDirection: 'row', justifyContent: 'space-between' },
  cardContainer: { maxWidth: '100% !important', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' },
  iconRow: { display: 'flex', flexDirection: 'row' },
  mb2: { marginBottom: '4px', marginRight: '16px' },
  iconOverlayImg: { position: 'absolute', zIndex: '1 !important' }
}));

const theme = createTheme({ palette: { primary: COLOR_PRIMARY, secondary: COLOR_SECONDARY, warning: COLOR_WARNING } });

const calcBalance = (items, voucherAmount) =>
  (voucherAmount ?? 0) - (Array.isArray(items) ? items.reduce((t, it) => t + (parseFloat(it && it.totalAmount) || 0), 0) : 0);

export const getUserName = person => (person && person !== undefined ? `${person.firstName} ${person.lastName}` : 'N/a');

const VoucherLineItemsCard = ({
  voucherInfo,
  voucherInfoForm,
  voucherLineItems = [],
  expenseCodes = [],
  normalizeAuthSummaries = [],
  enumerations,
  isReadOnly = false,
  onAddLine,
  onUpdateLine,
  onDeleteLine,
  setIsFormValidForApproval,
  statusIds,
  updateVoucher,
  setVoucherInfoForm,
  isDirty,
  setIsLoading,
  isLoading,
  payToVendor,
  updateParentVoucherAttachments,
  updateParentVoucherLineAttachments,
  exceptions,
  clientId,
  setExceptions,
  isAcumaticaRejected,
  pushVoucherToAcumaticaFunc,
  isFormValidForApproval,
  currencies = [],
}) => {
  const classes = useStyles();
  const voucherInfoDto = voucherInfoForm?.voucherInformation ?? {};

  const clientName = voucherInfoDto?.clientName ?? voucherInfo?.clientName;

  const voucherAmount = voucherInfoDto?.amount ?? voucherInfo?.amount ?? 0;

  const voucherInformation = voucherInfoForm?.voucherInformation ?? {};
  const bankingInformation = voucherInfoForm?.bankingInformation ?? {};

  const isValidCurrencyId = value => value !== undefined && value !== null && !isIdEqual(value, ZERO_GUID);

  const isValidExchangeRate = value =>
    value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value)) && Number(value) > 0;

  const isInternationalVoucher = isIdEqual(voucherInformation.voucherTypeId, INTERNATIONAL_VOUCHER_TYPE_ID);

  const isPaymentVoucher = isIdEqual(voucherInformation.voucherTypeId, PAYMENT_VOUCHER_TYPE_ID);

  const hasReceiptCurrency = isValidCurrencyId(voucherInformation.receiptCurrencyId);

  const hasPayInCurrency = isValidCurrencyId(voucherInformation.payInCurrencyId);

  const receiptCurrencyIsUSD = hasReceiptCurrency && isIdEqual(voucherInformation.receiptCurrencyId, USD_CURRENCY_ID);

  const payInCurrencyIsUSD = hasPayInCurrency && isIdEqual(voucherInformation.payInCurrencyId, USD_CURRENCY_ID);

  const hasCurrencyExchangeRate = isValidExchangeRate(voucherInformation.currencyExchangeRate);

  const hasPayInToUSDCurrencyExchangeRate = isValidExchangeRate(voucherInformation.payInToUSDCurrencyExchangeRate);

  const internationalVoucherHasBothUSDCurrencies = isInternationalVoucher && receiptCurrencyIsUSD && payInCurrencyIsUSD;

  const bankInformationIsValid = bankingInformation && bankingInformation.bankId && bankingInformation.bankId > 0;

  const hasRequiredInternationalCurrencies =
    hasReceiptCurrency && hasPayInCurrency && hasCurrencyExchangeRate && (payInCurrencyIsUSD || hasPayInToUSDCurrencyExchangeRate);

  const [items, setItems] = useState(voucherLineItems);
  const [expandedId, setExpandedId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newItem, setNewItem] = useState({
    paymentAuthorizationId: payToVendor ? null : voucherInfoForm?.payee?.authorizationId,
    expenseCodeId: null,
    expenseCode: '',
    totalAmount: '',
    description: '',
    onHoldDate: null,
    cat: ''
  });

  useEffect(() => setItems(voucherLineItems), [voucherLineItems]);
  const balance = useMemo(() => calcBalance(items, voucherAmount) - (addOpen ? parseFloat(newItem.totalAmount) || 0 : 0), [
    items,
    voucherAmount,
    addOpen,
    newItem.totalAmount
  ]);

  useEffect(() => {

    const internationalVoucherRequirementsMet = isInternationalVoucher ? bankInformationIsValid && hasRequiredInternationalCurrencies && !internationalVoucherHasBothUSDCurrencies : true;
    const paymentVoucherRequirementsMet = isPaymentVoucher ? bankInformationIsValid : true;

    const isValidForApproval =
      balance === 0 && internationalVoucherRequirementsMet && paymentVoucherRequirementsMet;

    setIsFormValidForApproval(isValidForApproval);
  }, [
    balance,
    voucherInformation.voucherTypeId,
    voucherInformation.receiptCurrencyId,
    voucherInformation.payInCurrencyId,
    voucherInformation.currencyExchangeRate,
    voucherInformation.payInToUSDCurrencyExchangeRate,
    isInternationalVoucher,
    hasRequiredInternationalCurrencies,
    internationalVoucherHasBothUSDCurrencies,
    setIsFormValidForApproval,
    bankInformationIsValid,
    bankingInformation
  ]);

  const codeSearchInfo = useMemo(() => CreateExpenseCodeSearchInfo(expenseCodes), [expenseCodes]);

  const filteredAuthorizations = useMemo(
    () => (clientName ? normalizeAuthSummaries.filter(a => a.clientName === clientName) : normalizeAuthSummaries),
    [normalizeAuthSummaries, clientName]
  );

  const resetNew = () => {
    setNewItem({
      paymentAuthorizationId: payToVendor ? null : voucherInfoForm?.payee?.authorizationId,
      expenseCodeId: null,
      expenseCode: '',
      totalAmount: '',
      description: '',
      onHoldDate: null,
      cat: ''
    });
  };

  const handleAddClick = () => {
    if (isReadOnly) return;
    if (!payToVendor) {
      setNewItem(prev => ({ ...prev, paymentAuthorizationId: voucherInfoForm?.payee?.authorizationId }));
    }
    setAddOpen(true);
  };

  const handleCancelAdd = () => {
    setAddOpen(false);
    resetNew();
  };

  const handleSaveNew = async () => {
    if (isReadOnly) return;
    setIsLoading(true);
    const voucherLineItemRow = {
      // ---- keep your existing values (unchanged) ----
      voucherId: voucherInfoForm?.voucherId ?? voucherInfo?.voucherId,
      paymentAuthorizationId: newItem.paymentAuthorizationId,
      expenseCodeId: newItem.expenseCodeId,
      totalAmount: Number(newItem.totalAmount || 0),
      description: newItem.description || '',
      onHoldDate: newItem.onHoldDate,
      lineItemStatusId: ZERO_GUID,
      payOption: false,
      approvedAmount: null,
      referenceString: null,
      glNumber: null,
      deniedReason: null,
      reportDate: null,
      payrollDate: null,
      dueDate: null,
      fromDate: null,
      toDate: null,
      entryDate: null,
      expenseDate: null,

      // ---- add defaults for the remaining C# properties (only defaults) ----
      lineItemId: null, // create: you don’t have one yet
      addedBy: null,
      addedOn: null,
      modifiedBy: null,
      modifiedOn: null
    };

    const payload = {
      voucherLineItemRow,
      expenseLineItemExceptionRow: {
        exceptionCommentId: 0,
        lineItemId: 0,
        isVoucherLineItem: true,
        isExpenseLineItem: false
      }
    };

    const saved = await onAddLine(payload, voucherInfoForm);
    if (saved) {
      setAddOpen(false);
      resetNew();
    }
    setIsLoading(false);
  };

  const handleDelete = async id => {
    if (isReadOnly) return;
    const ok = await onDeleteLine(id);
    if (ok) {
      setItems(prev => prev.filter(i => i.lineItemId !== id));
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Card className={classes.card} sx={{ mt: '10px' }}>
        {isLoading && <SpinnerComponent />}
        {!isLoading && (
          <Box>
            <CardContent className={classes.cardContainer}>
              <div className="row justify-space-between align-center" style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <div className={classes.iconRow}>
                  <FontAwesomeIcon icon={['fas', 'file-contract']} size="2x" />
                  <Typography variant="h5" sx={{ pl: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
                    Line Items
                  </Typography>
                </div>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <Typography>
                    Balance:&nbsp;
                    <span style={{ fontWeight: 'bold', color: balance === 0 ? undefined : 'red' }}>{receiptCurrencyIsUSD ? '$' : ''}{formatAmount(balance)}</span>
                  </Typography>
                </div>
                <div style={{ visibility: 'hidden', width: 250 }} />
              </div>
            </CardContent>

            <CardContent sx={{ p: '10px', ml: '10px', pt: 0 }}>
              <Box sx={{ mt: '16px' }}>
                <Paper>
                  <Box sx={{ p: '16px', pt: '8px' }}>
                    <Box sx={{ pb: '10px', borderBottom: '2px solid' }}>
                      <Grid container spacing={1} columns={20}>
                        <Grid item md={3}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            Authorization
                          </Typography>
                        </Grid>
                        <Grid item md={1}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            CAT
                          </Typography>
                        </Grid>
                        <Grid item md={3}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            Code
                          </Typography>
                        </Grid>
                        <Grid item md={2}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            Amount
                          </Typography>
                        </Grid>
                        <Grid item md={5}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            Line Desc
                          </Typography>
                        </Grid>
                        <Grid item md={2}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            On Hold
                          </Typography>
                        </Grid>
                        <Grid item md={2}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            Attachment
                          </Typography>
                        </Grid>
                        <Grid item md={1}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            {isReadOnly ? 'View' : 'Edit'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    {(!items || items.length === 0) && (
                      <Box sx={{ pt: 2, display: 'flex', justifyContent: 'center' }}>
                        <Typography variant="subtitle2">No line items. Please add at least one line item.</Typography>
                      </Box>
                    )}

                    {items?.map(row => (
                      <Box key={row.lineItemId} sx={{ pb: '10px', pt: '10px', borderBottom: '2px solid' }}>
                        {expandedId !== row.lineItemId ? (
                          <Grid container spacing={1} columns={20}>
                            <Grid item md={3}>
                              <Typography variant="body2" sx={{ pt: '10px', fontWeight: 500 }}>
                                {normalizeAuthSummaries.find(a => a.fileId === row.paymentAuthorizationId)?.transfereeName}
                              </Typography>
                            </Grid>
                            <Grid item md={1}>
                              <Typography variant="body2" sx={{ pt: '10px', fontWeight: 500 }}>
                                {categoryLabel(expenseCodes, row.expenseCodeId)}
                              </Typography>
                            </Grid>
                            <Grid item md={1}>
                              <Typography variant="body2" sx={{ pt: '10px', fontWeight: 500 }}>
                                {codeFromId(expenseCodes, row.expenseCodeId)}
                              </Typography>
                            </Grid>
                            <Grid item md={2}>
                              <Typography variant="body2" sx={{ pt: '10px', fontWeight: 500 }}>
                                {descriptionFromId(expenseCodes, row.expenseCodeId)}
                              </Typography>
                            </Grid>
                            <Grid item md={2}>
                              <Typography variant="body2" sx={{ pt: '10px', fontWeight: 500 }}>
                                {receiptCurrencyIsUSD ? '$' : ''} {formatAmount(row.totalAmount)}
                              </Typography>
                            </Grid>
                            <Grid item md={5}>
                              <Typography variant="body2" sx={{ pt: '10px', fontWeight: 500 }}>
                                {row.description}
                              </Typography>
                            </Grid>
                            <Grid item md={2}>
                              <Checkbox checked={!!row.onHoldDate} disabled />
                            </Grid>
                            <Grid item md={2}>
                              <AttachmentModal
                                displayText={`View Attachments (${voucherInfoForm && voucherInfoForm.voucherLineAttachments
                                  ? voucherInfoForm.voucherLineAttachments.filter(vla => vla.voucherLineItemId === row.lineItemId).length
                                  : 0
                                  })`}
                                isReadOnly={isReadOnly}
                                voucherLineInfo={voucherInfoForm.voucherInformation}
                                voucherAttachments={voucherInfoForm.voucherAttachments}
                                voucherLineAttachments={
                                  voucherInfoForm && voucherInfoForm.voucherLineAttachments
                                    ? voucherInfoForm.voucherLineAttachments.filter(vla => vla.voucherLineItemId === row.lineItemId)
                                    : []
                                }
                                updateParentVoucherAttachments={updateParentVoucherAttachments}
                                updateParentVoucherLineAttachments={updateParentVoucherLineAttachments}
                                lineItemId={row.lineItemId}
                              />
                            </Grid>
                            <Grid item md={1}>
                              <Fab
                                className={classes.iconOverlayImg}
                                color="primary"
                                size="small"
                                onClick={() => setExpandedId(row.lineItemId)}
                                disabled={addOpen}
                              >
                                <FontAwesomeIcon size="1x" icon={['fas', 'pen']} color="#fff" />
                              </Fab>
                            </Grid>
                            <Box sx={{ pl: 1, pt: 1 }}>
                              <Button
                                color="primary"
                                variant="contained"
                                sx={{ backgroundColor: '#36939b', '&:hover': { backgroundColor: '#15585e' }, color: '#fff', minWidth: 120 }}
                                onClick={() => handleDelete(row.lineItemId)}
                                disabled={isReadOnly}
                              >
                                Delete Line
                              </Button>
                            </Box>
                          </Grid>
                        ) : (
                          <VoucherReportLineItem
                            isReadOnly={isReadOnly}
                            lineItem={row}
                            closeLineItem={() => setExpandedId(null)}
                            expenseCodes={expenseCodes}
                            authorizationSummaries={filteredAuthorizations}
                            voucherInfoForm={voucherInfoForm ?? voucherInfo}
                            onUpdateLine={onUpdateLine}
                            setFormExpenseLineItems={setItems}
                            payToVendor={payToVendor}
                            updateParentVoucherAttachments={updateParentVoucherAttachments}
                            updateParentVoucherLineAttachments={updateParentVoucherLineAttachments}
                            exceptions={exceptions ? exceptions.exceptionSumamries : []}
                            setExceptions={setExceptions}
                            expenseLineItemExceptions={voucherInfoForm.expenseLineItemExceptions}
                            clientId={clientId}
                            enumerations={enumerations}
                            authId={row.paymentAuthorizationId}
                            //voucherInfoForm={voucherInfoForm}
                            currencies={currencies}
                            receiptCurrencyIsUSD={receiptCurrencyIsUSD}
                            payInCurrencyIsUSD={payInCurrencyIsUSD}
                          />
                        )}
                      </Box>
                    ))}
                    {addOpen && (
                      <Box sx={{ pb: '10px', pt: '10px', borderBottom: '2px solid' }}>
                        <Grid container spacing={1} columns={20}>
                          <Grid item md={3}>
                            {payToVendor ? (
                              <AdvancedSearch
                                labelText={'Authorization'}
                                displayValueAccessorKey={'transfereeName'}
                                saveIdValueAccessorKey={'fileId'}
                                searchingCriteria={[
                                  { name: 'fileNumber', title: 'Auth Id' },
                                  { name: 'transfereeName', title: 'Full Name' },
                                  { name: 'departure', title: 'Departure' },
                                  { name: 'destination', title: 'Destination' },
                                  { name: 'clientName', title: 'Client' }
                                ]}
                                searchingCriteriaExtensions={[
                                  { columnName: 'fileNumber', width: 150 },
                                  { columnName: 'transfereeName', width: 250 },
                                  { name: 'departure', width: 175 },
                                  { name: 'destination', width: 175 },
                                  { columnName: 'clientName', width: 175 }
                                ]}
                                searchingRows={filteredAuthorizations}
                                id={newItem.paymentAuthorizationId}
                                value={filteredAuthorizations.find(a => a.fileId === newItem.paymentAuthorizationId)?.transfereeName}
                                setIdValue={id => setNewItem(prev => ({ ...prev, paymentAuthorizationId: id }))}
                                isReadOnly={isReadOnly}
                              />
                            ) : (
                              <TextField
                                disabled
                                value={normalizeAuthSummaries.find(a => a.fileId === newItem.paymentAuthorizationId)?.transfereeName}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                          </Grid>

                          <Grid item md={1}>
                            <Box sx={{ pt: '10px' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {categoryLabel(expenseCodes, newItem.expenseCodeId)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item md={2}>
                            {(() => {
                              const info = codeSearchInfo;
                              return (
                                <AdvancedSearch
                                  labelText=""
                                  displayValueAccessorKey={info.nameKey}
                                  saveIdValueAccessorKey={info.idKey}
                                  searchingCriteria={info.searchingColumns}
                                  searchingCriteriaExtensions={info.searchingColumnExtensions}
                                  searchingRows={info.searchingRows}
                                  id={newItem.expenseCodeId}
                                  value={codeFromId(expenseCodes, newItem.expenseCodeId)}
                                  setIdValue={(id, displayValue) => {
                                    const ec = ecById(expenseCodes, id);
                                    setNewItem(s => ({
                                      ...s,
                                      expenseCodeId: id,
                                      expenseCode: displayValue,
                                      cat: ec && ec.category ? ec.category : s.cat
                                    }));
                                  }}
                                  maxWidthsx="360px"
                                  isReadOnly={isReadOnly}
                                />
                              );
                            })()}
                          </Grid>
                          <Grid item md={2}>
                            <Box sx={{ pt: '10px' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {descriptionFromId(expenseCodes, newItem.expenseCodeId)}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item md={2}>
                            <TextField
                              value={newItem.totalAmount}
                              fullWidth
                              type="number"
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                startAdornment: receiptCurrencyIsUSD ? (
                                  <InputAdornment position="start">$</InputAdornment>
                                ) : null
                              }} disabled={isReadOnly}
                              onChange={e => setNewItem(prev => ({ ...prev, totalAmount: e.target.value }))}
                            />
                          </Grid>

                          <Grid item md={5}>
                            <TextField
                              value={newItem.description}
                              fullWidth
                              disabled={isReadOnly}
                              InputLabelProps={{ shrink: true }}
                              onChange={e => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                            />
                          </Grid>

                          <Grid item md={2} sx={{ ml: '10px' }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!newItem.onHoldDate}
                                  disabled={isReadOnly}
                                  onChange={() => setNewItem(prev => ({ ...prev, onHoldDate: prev.onHoldDate ? null : new Date() }))}
                                />
                              }
                              label="On Hold"
                            />
                          </Grid>

                          {/* <Grid item md={2}>
                            <AttachmentModal displayText={'View Attachment'} voucherLineInfo={newItem} updateParentVoucherAttachments={updateParentVoucherAttachments} updateParentVoucherLineAttachments={updateParentVoucherLineAttachments} />
                          </Grid> */}

                          <Grid item md={7} />
                          <Grid item md={10}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
                              <Button variant="contained" onClick={handleCancelAdd}>
                                Cancel
                              </Button>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSaveNew}
                                disabled={
                                  isReadOnly ||
                                  !newItem.paymentAuthorizationId ||
                                  !newItem.expenseCodeId ||
                                  Number(newItem.totalAmount || 0) <= 0
                                }
                              >
                                Save Line
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '10px' }}>
                      <Button
                        color="primary"
                        variant="contained"
                        sx={{ backgroundColor: '#36939b', '&:hover': { backgroundColor: '#15585e' }, color: '#fff', minWidth: 120 }}
                        onClick={handleAddClick}
                        disabled={isReadOnly || addOpen || !!expandedId}
                      >
                        Add Line
                      </Button>
                    </Box>
                  </Box>
                  <Box sx={{ mt: '16px', display: 'flex', justifyContent: 'space-between', px: '24px', pb: '10px' }}>
                    <VoucherDetailsButtons
                      isFormValidForApproval={isFormValidForApproval}
                      statusIds={statusIds}
                      voucherStatusId={
                        voucherInfoForm?.voucherInformation?.voucherStatusId ?? voucherInfo?.voucherInformation?.voucherStatusId ?? ''
                      }
                      voucherInfoForm={voucherInfoForm}
                      voucherInfo={voucherInfo}
                      isDirty={isDirty && !addOpen && !expandedId}
                      updateVoucher={updateVoucher}
                      setVoucherInfoForm={setVoucherInfoForm}
                      isAcumaticaRejected={isAcumaticaRejected}
                      pushVoucherToAcumaticaFunc={pushVoucherToAcumaticaFunc}
                      addOpen={addOpen}
                      expandedId={expandedId}
                      isReadOnly={isReadOnly}
                    />
                  </Box>
                </Paper>
              </Box>
            </CardContent>
          </Box>
        )}
      </Card>
    </ThemeProvider>
  );
};

export default VoucherLineItemsCard;
